import { monitorForElements } from "@msuite/ui/dnd";
import { useTranslation } from "@msuite/ui/localization";
import { useEffect, useMemo } from "react";
import type { DraggedCrew } from "@/components/plan/dragged-crew";
import { usePlanStore } from "@/components/plan/store";
import { useAuth } from "@/hooks/use-auth";
import { useMembers } from "@/hooks/use-members";
import { useProjects } from "@/hooks/use-projects";
import { useScheduledTickets } from "@/hooks/use-scheduled-tickets";
import { CapacityCalendar, useCapacityCalendar } from "../capacity-calendar";
import { CapacityManagerRow } from "./capacity-manager-row";
import { CapacityManagerSectionRow } from "./capacity-manager-section-row";
import type {
	CapacityManagerList,
	CapacityManagerListItem,
	CapacityProjectList,
	ScheduledTicketList,
} from "./types";

/** Props Interface */
interface CapacityManagerViewProps {
	searchValue?: string;
}

/**
 * The capacity board seen from the people who carry the projects, three levels deep: every member
 * holding the `manage-project` capability, the projects of theirs the window has work on, and the
 * tickets that put them there.
 *
 * **Every manager is listed, whatever the window holds.** A manager with nothing on in these weeks
 * is the answer somebody is looking for — who is free — so the roster stays still while the week
 * picker moves and only the levels beneath it change.
 *
 * The signed-in manager leads the roster, under no heading of its own, and the rest follow beneath
 * a separator — the reader's own work is what they open the board for.
 */
export function CapacityManagerView({ searchValue }: CapacityManagerViewProps) {
	/** Store */
	const setDraggedCrew = usePlanStore((state) => state.setDraggedCrew);

	/** Hooks */
	const { t } = useTranslation();
	const controller = useCapacityCalendar();
	const { user } = useAuth();

	/** Query */
	const { members, isLoading } = useMembers({ capabilities: ["manage-project"] });
	const { projects } = useProjects();
	const { tickets } = useScheduledTickets(controller.windowStart, controller.windowEnd);

	/** Effects */
	// One monitor for the whole board rather than one per row. A crew card leaving the resources
	// panel is published to the store and every ticket row reads it from there, so a drag costs one
	// registration however many rows are open — and the rows dim on the store's change, not on a
	// listener each of them would otherwise have to hold.
	useEffect(() => {
		return monitorForElements({
			onDragStart({ source }) {
				if (source.data.type !== "crew-definition") return;
				setDraggedCrew(source.data.item as DraggedCrew);
			},
			// Fires on a cancelled drag as well, so nothing stays dimmed after Escape.
			onDrop() {
				setDraggedCrew(null);
			},
		});
	}, [setDraggedCrew]);

	/** Computed */
	const ticketsByProject = useMemo(() => {
		const byProject = new Map<string, ScheduledTicketList>();
		for (const ticket of tickets) {
			const existing = byProject.get(ticket.projectId);
			if (existing) existing.push(ticket);
			else byProject.set(ticket.projectId, [ticket]);
		}
		return byProject;
	}, [tickets]);

	// `useProjects` asks for the active ones, and `unique_user_per_project` keeps a user to one
	// membership per project, so a project reaches a given manager's list exactly once. A project
	// with no ticket the window reaches is left out entirely: there is nothing under it to open.
	const projectsByUser = useMemo(() => {
		const byUser = new Map<string, CapacityProjectList>();
		for (const project of projects ?? []) {
			const projectTickets = ticketsByProject.get(project.id);
			if (!projectTickets?.length) continue;
			for (const member of project._members ?? []) {
				const item = {
					projectId: project.id,
					name: project.name,
					number: project.number,
					city: project.city,
					weekdays: project.weekdays,
					tickets: projectTickets,
				};
				const existing = byUser.get(member.userId);
				if (existing) existing.push(item);
				else byUser.set(member.userId, [item]);
			}
		}
		for (const list of byUser.values()) {
			list.sort((a, b) => a.name.localeCompare(b.name));
		}
		return byUser;
	}, [projects, ticketsByProject]);

	const rows = useMemo<CapacityManagerList>(() => {
		const search = searchValue?.toLowerCase().trim();
		const result: CapacityManagerList = [];
		for (const member of members) {
			const fullName = `${member.user.firstName ?? ""} ${member.user.lastName ?? ""}`.trim();
			if (search && !fullName.toLowerCase().includes(search)) continue;
			result.push({
				userId: member.user.id,
				fullName,
				projects: projectsByUser.get(member.user.id) ?? [],
			});
		}
		return result;
	}, [members, projectsByUser, searchValue]);

	// Whoever is reading holds the first line. They are absent from it whenever they carry no
	// `manage-project` capability or the search filtered them out, and then the roster is one plain
	// list with no separator to explain.
	const { ownRow, otherRows } = useMemo(() => {
		const others: CapacityManagerList = [];
		let own: CapacityManagerListItem | null = null;
		for (const row of rows) {
			if (user?.id && row.userId === user.id) own = row;
			else others.push(row);
		}
		return { ownRow: own, otherRows: others };
	}, [rows, user?.id]);

	/** Render */
	return (
		<CapacityCalendar
			isLoading={isLoading}
			isEmpty={!rows.length}
		>
			{ownRow && (
				<CapacityManagerRow
					key={ownRow.userId}
					item={ownRow}
				/>
			)}
			{ownRow && otherRows.length > 0 && (
				<CapacityManagerSectionRow label={t("planning.other_managers")} />
			)}
			{otherRows.map((row) => (
				<CapacityManagerRow
					key={row.userId}
					item={row}
				/>
			))}
		</CapacityCalendar>
	);
}
